import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Avatar,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Divider
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

// استيراد الشعار الخاص بالطباعة
import logo from '../image/depLogo.png'; 

// استيراد المكونات والخيارات
import { RATING_OPTIONS } from '../features/ratingOptions'; 
import { CenterContent, MasonryLayout, PageContainer, VList } from '../utils/layouts';
import AssessmentView from '../features/AssessmentView'; 
import Loader from '../utils/Loader';

const StudentAssessments = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  // ===== حالات الفلاتر =====
  const [filterStatus, setFilterStatus] = useState("unassigned"); // الافتراضي: لم يتم التعيين
  const [filterLevel, setFilterLevel] = useState("");

  // ===== حالات بيانات الطباعة =====
  const [teacherName, setTeacherName] = useState("");
  const [schoolName, setSchoolName] = useState("");

  // جلب بيانات المعلم والمدرسة
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setTeacherName(parsedUser?.fullName || "غير متوفر");
        setSchoolName(parsedUser?.school?.schoolName || "غير متوفر");
      }
    } catch (err) {
      console.error("Error reading from localStorage", err);
    }
  }, []);

  useEffect(() => {
    if (studentId) {
      fetchAssessments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      
      const response = await axios.get(`${BASE_URL}assessment-results`, {
        params: {
          studentId: studentId,
          perPage: 10000,
          page: 1
        },
        headers: {
          Authorization: `Bearer ${token}` 
        }
      });
      
      const data = response.data?.data || [];
      setAssessments(data);

    } catch (error) {
      console.error("حدث خطأ أثناء جلب تقييمات الطالب:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "غير محدد";
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getRatingDetails = (levelValue) => {
    return RATING_OPTIONS.find(opt => String(opt.value) === String(levelValue));
  };

  // دالة لجلب الأيقونة الملونة للطباعة
  const getRatingIcon = (levelValue) => {
    const option = getRatingDetails(levelValue);
    if (option && option.icon) {
      const IconComponent = option.icon;
      return <IconComponent size={20} color={option.color} />;
    }
    return levelValue; 
  };

  const currentStudentName = useMemo(() => {
    if (assessments.length > 0) {
      return assessments[0]?.student?.full_name || assessments[0]?.studentName || "غير متوفر";
    }
    return "غير متوفر";
  }, [assessments]);

  // ===== منطق الفلاتر والعدادات =====

  const counts = useMemo(() => {
    if (!assessments) return { all: 0, unassigned: 0, assigned: 0 };
    return {
      all: assessments.length,
      unassigned: assessments.filter(item => item.level === item.result).length,
      assigned: assessments.filter(item => item.level !== item.result).length,
    };
  }, [assessments]);

  const ratingCounts = useMemo(() => {
    const countsMap = {};
    RATING_OPTIONS.forEach(opt => {
      countsMap[opt.value] = 0;
    });

    if (assessments && assessments.length > 0) {
      assessments.forEach(item => {
        if (item.level) {
          countsMap[item.level] = (countsMap[item.level] || 0) + 1;
        }
      });
    }
    return countsMap;
  }, [assessments]);

  const filteredAssessments = useMemo(() => {
    if (!assessments) return [];

    return assessments.filter((item) => {
      let matchesStatus = true;
      if (filterStatus === "unassigned") {
        matchesStatus = item.level === item.result;
      } else if (filterStatus === "assigned") {
        matchesStatus = item.level !== item.result;
      }

      const matchesLevel = filterLevel ? String(item.level) === String(filterLevel) : true;

      return matchesStatus && matchesLevel;
    });
  }, [assessments, filterStatus, filterLevel]);

  const handlePrint = () => {
    window.print();
  };

  // تنسيق مشترك للأزرار (Cards/Chips) لتوحيد الشكل
  const toggleButtonStyles = {
    px: 2, 
    py: 1, 
    fontWeight: 'bold',
    display: 'flex', 
    alignItems: 'center', 
    gap: 1,
    border: '1px solid #cbd5e1 !important',
    borderRadius: '8px !important',
    margin: '0 !important',
    bgcolor: 'white',
    whiteSpace: 'nowrap', // لمنع النص من النزول لسطر جديد
    '&.Mui-selected': {
      bgcolor: '#e2e8f0', 
      borderColor: '#94a3b8 !important',
      color: 'text.primary'
    },
    '&:hover': {
      bgcolor: '#f1f5f9',
    }
  };

  return (
    <CenterContent sx={{ minHeight: '100vh'}}>
      <PageContainer>
        <VList>
          
          {/* ========================================================= */}
          {/* 1. واجهة العرض العادية (تُخفى عند الطباعة)                  */}
          {/* ========================================================= */}
          <Box sx={{ '@media print': { display: 'none' }, width: '100%' }}>
            
            {/* شريط العنوان العلوي */}
            <Paper variant='section' sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              mb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 50, height: 50, mr: 2, ml: 2 }}>
                  <AssignmentTurnedInIcon />
                </Avatar>
                <Typography variant="h5" color="text.primary" fontWeight="bold">
                  التقييمات السابقة
                </Typography>
              </Box>

              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<PrintIcon />} 
                onClick={handlePrint}
                disabled={loading || filteredAssessments.length === 0}
              >
                طباعة التقرير
              </Button>
            </Paper>

            {/* منطقة الفلاتر - شريط واحد (سطر واحد) */}
            {!loading && assessments.length > 0 && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 1.5, 
                  mb: 3, 
                  borderRadius: 2, 
                  backgroundColor: '#f8fafc', 
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  overflowX: 'auto', // للسماح بالتمرير الأفقي في الشاشات الصغيرة
                  '&::-webkit-scrollbar': { height: '6px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '10px' }
                }}
              >
                
                {/* 1. أزرار حالة التعيين */}
                <ToggleButtonGroup
                  value={filterStatus}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) setFilterStatus(newValue);
                  }}
                  aria-label="Assessment Status"
                  sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}
                >
                  <ToggleButton value="all" sx={toggleButtonStyles}>
                    الكل ({counts.all})
                  </ToggleButton>
                  <ToggleButton value="assigned" sx={toggleButtonStyles}>
                    تم التعيين ({counts.assigned})
                  </ToggleButton>
                  <ToggleButton value="unassigned" sx={toggleButtonStyles}>
                    لم يتم التعيين ({counts.unassigned})
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* فاصل عمودي بين المجموعتين */}
                <Divider orientation="vertical" flexItem sx={{ borderColor: '#cbd5e1', my: 1 }} />

                {/* 2. أزرار التقييمات بالأيقونات */}
                <ToggleButtonGroup
                  value={filterLevel}
                  exclusive
                  onChange={(e, newValue) => {
                    setFilterLevel(newValue !== null ? newValue : "");
                  }}
                  aria-label="Filter by Rating"
                  sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}
                >
                  <ToggleButton value="" sx={toggleButtonStyles}>
                    كل التقييمات
                  </ToggleButton>

                  {RATING_OPTIONS.map((option) => {
                    const IconComponent = option.icon;
                    const count = ratingCounts[option.value] || 0;
                    
                    return (
                      <ToggleButton 
                        key={option.value} 
                        value={String(option.value)} 
                        sx={toggleButtonStyles}
                        title={option.label}
                      >
                        {IconComponent && <IconComponent size={22} color={count > 0 ? option.color : '#b0bec5'} />}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '15px',
                            color: count > 0 ? 'text.primary' : 'text.disabled' 
                          }}
                        >
                          ({count})
                        </Typography>
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
                
              </Paper>
            )}

            {/* منطقة عرض التقييمات */}
            {loading ? (
              <Loader/>
            ) : (
              <>
                {assessments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 8, p: 5, width: '100%' }}>
                    <AssignmentTurnedInIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      لا توجد تقييمات مسجلة لهذا الطالب حتى الآن.
                    </Typography>
                  </Box>
                ) : filteredAssessments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 5, width: '100%' }}>
                    <Typography variant="body1" color="text.secondary">
                      لا توجد نتائج مطابقة للفلتر الحالي.
                    </Typography>
                  </Box>
                ) : (
                  <MasonryLayout>
                    {filteredAssessments.map((assessment) => (
                      <AssessmentView 
                        key={assessment.id} 
                        assessment={assessment} 
                        ratingInfo={getRatingDetails(assessment.level)} 
                        formatDate={formatDate}
                      />
                    ))}
                  </MasonryLayout>
                )}
              </>
            )}
          </Box>


          {/* ========================================================= */}
          {/* 2. واجهة الطباعة (تظهر فقط عند الطباعة)                     */}
          {/* ========================================================= */}
          <Box 
            sx={{ 
              display: 'none', 
              '@media print': { 
                display: 'block',
                direction: 'rtl',
                margin: '0',
                width: '100%',
              } 
            }}
          >
            <style type="text/css" media="print">
              {`
                @page { 
                  size: A4 landscape; 
                  margin: 10mm 15mm 15mm 15mm; 
                }
                body { 
                  -webkit-print-color-adjust: exact !important; 
                  print-color-adjust: exact !important; 
                  background-color: white;
                }
                ::-webkit-scrollbar { display: none; }
                .print-header { border-bottom: 2px solid #1976d2; padding-bottom: 12px; margin-bottom: 20px; }
                .print-table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
                .print-table tr { page-break-inside: avoid; page-break-after: auto; }
                .print-table thead { display: table-header-group; }
                .print-table tfoot { display: table-footer-group; }
                .print-table thead th, .print-table tbody td {
                  border: 1px solid #555 !important; padding: 8px 6px !important; font-size: 13px !important; vertical-align: middle;
                }
                .print-table thead th { background-color: #e3f2fd !important; color: #000 !important; font-weight: bold; font-size: 14px !important; text-align: center; }
                .page-footer-print {
                  position: fixed; bottom: 0; left: 0; width: 100%; display: flex; justify-content: flex-start; align-items: center;
                  gap: 12px; padding: 5px 25px; background-color: white; opacity: 0.65;
                }
                .footer-space { height: 55px; }
              `}
            </style>

            <Box className="print-header">
              <Typography variant="h5" align="center" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                سجل التقييمات الوصفية للطالب
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mt: 2 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '15px', color: '#333' }}>
                  المدرسة: <span style={{fontWeight: 'normal'}}>{schoolName}</span>
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '15px', color: '#333' }}>
                  المعلم: <span style={{fontWeight: 'normal'}}>{teacherName}</span>
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '15px', color: '#333' }}>
                  الطالب: <span style={{fontWeight: 'normal', color: '#d32f2f'}}>{currentStudentName}</span>
                </Typography>
              </Box>
            </Box>

            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>ت</th>
                  <th style={{ width: '12%' }}>التاريخ</th>
                  <th style={{ width: '20%', textAlign: 'right' }}>عنوان الدرس</th>
                  <th style={{ width: '20%', textAlign: 'right' }}>محور التقييم</th>
                  <th style={{ width: '6%' }}>التقييم</th>
                  <th style={{ width: '38%', textAlign: 'right' }}>العبارة الوصفية</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.map((item, index) => {
                  const isUnassigned = item.level === item.result;
                  return (
                    <tr key={item.id}>
                      <td style={{ textAlign: 'center' }}>{index + 1}</td>
                      <td style={{ textAlign: 'center' }} dir="ltr">{formatDate(item.createdAt || item.date || item.time)}</td>
                      <td style={{ textAlign: 'right' }}>{item?.lessonTitle || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{item?.assessmentCriteria?.title || '-'}</td>
                      <td style={{ textAlign: 'center' }}>{getRatingIcon(item.level)}</td>
                      <td style={{ textAlign: 'right', lineHeight: '1.6' }}>
                        {isUnassigned ? "لم يتم التعيين بعد" : item.result}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ border: 'none !important', padding: '0 !important' }}>
                    <div className="footer-space"></div>
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="page-footer-print">
              <img src={logo} alt="logo" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
              <Typography sx={{ fontWeight: 'bold', fontSize: '15px', color: '#0b807b' }}>
                مجموعة مدارس العميد التعليمية
              </Typography>
            </div>

          </Box>
        </VList>
      </PageContainer>
    </CenterContent>
  );
};

export default StudentAssessments;